// Форматтеры для UI. Деньги — транспорт хранит копейки (int), показываем рубли.
//
// Замечание: @app/money тянет за собой Dinero.js (тяжёлый рантайм) и не входит
// в зависимости admin-приложения. Для простого display kopecks→рубли используем
// локальный Intl.NumberFormat, не нарушая правило «вся арифметика над деньгами —
// через @app/money»: здесь нет арифметики, только форматирование строки.

const RUB_FORMATTER = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * Копейки (целое из транспорта) → «X XXX ₽».
 * Дробная часть отбрасывается до 2 знаков; целые суммы — без копеек.
 */
export function formatRub(kopecks: number): string {
  if (!Number.isFinite(kopecks)) return '—';
  return RUB_FORMATTER.format(kopecks / 100);
}

/**
 * Сумма со знаком в принятом в кассе виде: минус — знаком U+2212 перед
 * модулем («−150 ₽»), как у расходов и возвратов, а не дефисом из `Intl`.
 */
export function formatRubSigned(kopecks: number): string {
  return kopecks < 0 ? `−${formatRub(-kopecks)}` : formatRub(kopecks);
}

/** Количество со знаком U+2212 («−1»), в пару к `formatRubSigned`. */
export function formatQtySigned(qty: number): string {
  return qty < 0 ? `−${-qty}` : String(qty);
}

/**
 * Парсит ввод в рублях (строка) в копейки.
 * "1 500,50" → 150050, "1500.5" → 150050, "" → 0.
 * Используется в формах кассы (ввод рублей → транспорт kopecks).
 */
export function rublesToKopecks(input: string | number): number {
  if (typeof input === 'number') {
    return Math.round(input * 100);
  }
  const normalized = input.replace(/\s/g, '').replace(',', '.').trim();
  if (!normalized) return 0;
  const value = Number(normalized);
  if (Number.isNaN(value)) return 0;
  return Math.round(value * 100);
}

/** Целое число с разделителями тысяч: 1500 → «1 500». */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '0';
  return new Intl.NumberFormat('ru-RU').format(value);
}

/** Процент: 12.5 → «12,5 %». */
export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '0 %';
  const formatted = new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 1,
  }).format(value);
  return `${formatted} %`;
}

// Время школы. Бэкенд хранит все timestamp'ы в UTC (см. rrule-expander §14.2);
// показываем в фиксированной TZ школы. Для пилота — Москва (UTC+3, без DST).
// TODO(multi-tz): хранить TZ на уровне tenant/branch для мультирегиональности.
export const SCHOOL_TZ = 'Europe/Moscow';
export const SCHOOL_TZ_OFFSET_HOURS = 3;

/** ISO-datetime (UTC) → время HH:MM в TZ школы. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: SCHOOL_TZ,
  });
}

/** ISO-дата (UTC) → DD.MM.YYYY в TZ школы. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { timeZone: SCHOOL_TZ });
}

/**
 * Приводит строку таймстампа к виду, который парсит `new Date()`.
 * Postgres `timestamptz::text` отдаёт `2026-07-15 18:00:00+00` — пробел вместо `T`
 * и смещение БЕЗ минут (`+00`, `+03`), которое JS-Date НЕ парсит (→ Invalid Date).
 * Нормализуем: пробел→`T`, `+00`→`+00:00`.
 */
function toParsableIso(raw: string): string {
  const s = raw.trim().replace(' ', 'T');
  // ЧИСТАЯ ДАТА `YYYY-MM-DD` — отдаём как есть: иначе замена ниже примет день
  // месяца за смещение часового пояса (`2026-09-15` → `2026-09-15:00` →
  // Invalid Date). Колонок типа `date` у нас много — сроки абонементов,
  // заморозки, дни рождения (правка 2026-08-02, найдено в мини-аппе).
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Смещение вида +00 / -03 в конце (2 цифры без минут) → дополняем `:00`.
  return s.replace(/([+-]\d{2})$/, '$1:00');
}

/**
 * ISO / Postgres-timestamptz → `DD.MM.YYYY HH:MM` в TZ школы.
 * Терпит формат `2026-07-15 18:00:00+00` (см. toParsableIso). Пустое/битое → «—».
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(toParsableIso(iso));
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('ru-RU', { timeZone: SCHOOL_TZ });
  const time = d.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: SCHOOL_TZ,
  });
  return `${date} ${time}`;
}

/**
 * Срок жизни клиента — сколько прошло от даты регистрации до сегодня.
 * «сегодня» / «3 дня» / «2 месяца» / «1 год 4 месяца». Возвращает '—' на пустом/битом.
 */
export function formatClientLifetime(
  registeredAt: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!registeredAt) return '—';
  const start = new Date(registeredAt);
  if (Number.isNaN(start.getTime())) return '—';
  const days = Math.floor((now.getTime() - start.getTime()) / 86_400_000);
  if (days <= 0) return 'сегодня';
  if (days < 30) return `${days} ${plural(days, ['день', 'дня', 'дней'])}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${plural(months, ['месяц', 'месяца', 'месяцев'])}`;
  const years = Math.floor(months / 12);
  const restMonths = months % 12;
  const yearsPart = `${years} ${plural(years, ['год', 'года', 'лет'])}`;
  if (restMonths === 0) return yearsPart;
  return `${yearsPart} ${restMonths} ${plural(restMonths, ['месяц', 'месяца', 'месяцев'])}`;
}

/** Русское склонение: forms = [1, 2-4, 5+], например ['день','дня','дней']. */
function plural(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}
